export type Level = "critical" | "high" | "ok" | "unknown";
export type Severity = "critical" | "high" | "medium" | "low";

export interface CompanySummary {
  siren: string;
  name: string | null;
  city: string | null;
  postal_code: string | null;
  active: boolean | null;
  activity: string | null;
  created_on: string | null;
}

export interface Situation {
  level: Level;
  label: string;
  since: string | null;
}

export interface CompanyEvent {
  id: number;
  source: string;
  kind: string;
  kind_label: string;
  severity: Severity;
  title: string;
  published_on: string | null;
  url: string | null;
  /** Official BODACC notice as a PDF, when the source publishes one. */
  document_url: string | null;
  details: Record<string, unknown>;
  is_history: boolean;
  detected_at: string;
}

export interface CategoryRef {
  id: number;
  name: string;
}

export interface Category extends CategoryRef {
  watch_count: number;
}

export interface WatchItem {
  id: number;
  siren: string;
  name: string | null;
  label: string | null;
  city: string | null;
  created_at: string;
  last_checked_at: string | null;
  situation: Situation;
  latest_event: CompanyEvent | null;
  recent_alerts: number;
  categories: CategoryRef[];
}

export interface RegistryFact {
  key: string;
  label: string;
  value: string | string[] | null;
}

export interface SourceStatus {
  source: string;
  last_success_at: string | null;
  last_error_at: string | null;
  last_error: string | null;
}

export interface FinanceYear {
  year: string;
  /** Chiffre d'affaires, in euros. */
  ca: number | null;
  resultat_net: number | null;
}

export interface CompanyDocument {
  type: "acte" | "bilan";
  id: string;
  date: string | null;
  label: string;
  confidential: boolean;
  /** Same-origin path to the PDF; null when the document is confidential. */
  download_url: string | null;
}

export interface MarketQuote {
  symbol: string;
  currency: string | null;
  last_close: number;
  as_of: string;
  /** Day change as a fraction (-0.012 = −1,2 %). */
  change_pct: number | null;
  /** [ISO date, close] pairs. */
  series: [string, number][];
  url: string;
}

export interface CompanyDetail {
  siren: string;
  name: string | null;
  watch: WatchItem;
  situation: Situation;
  registry: RegistryFact[];
  sources: SourceStatus[];
  events: CompanyEvent[];
  public_url: string;
  /** Our endpoint for the INSEE avis de situation; null without a head office SIRET. */
  avis_situation_url: string | null;
  /** Opened in the browser on data.inpi.fr. */
  inpi_extract_url: string;
  /** Newest first, up to 5 years. */
  finances: FinanceYear[];
  /** Newest first. */
  documents: CompanyDocument[];
  /** Null when the company is not listed. */
  market: MarketQuote | null;
}

export interface Me {
  email: string;
  confirmed: boolean;
  /** False while an administrator has not validated the account. */
  approved: boolean;
  is_admin: boolean;
  watch_count: number;
  /** Event kinds e-mailed to this user. */
  alert_kinds: string[];
  /** True when every kind is wanted, including kinds added later. */
  all_alerts: boolean;
  /** True when alerts can no longer be delivered to the account e-mail (bounce, spam report…). */
  alert_suppressed: boolean;
  /** Extra alert recipients the account may add, besides its own address. */
  max_alert_recipients: number;
}

export type SuppressionReason = "hard_bounce" | "invalid_email" | "blocked" | "spam" | "unsubscribed";

export interface Recipient {
  /** Null for the account e-mail, which is always a recipient. */
  id: number | null;
  email: string;
  is_account_email: boolean;
  /** "suppressed" wins over pending/active. */
  status: "pending" | "active" | "suppressed";
  suppression: {
    reason: SuppressionReason;
    at: string;
    /** False for spam reports and unsubscriptions: only an administrator can lift them. */
    can_retry: boolean;
  } | null;
  confirmed_at: string | null;
  created_at: string | null;
}

export type AlertStatus =
  | "sent"
  | "failed"
  | "deferred"
  | "soft_bounce"
  | "delivered"
  | "opened"
  | "clicked"
  | "hard_bounce"
  | "invalid_email"
  | "blocked"
  | "spam"
  | "unsubscribed"
  | "error";

export interface AlertDelivery {
  id: number;
  sent_at: string;
  recipient: string;
  subject: string;
  status: AlertStatus;
  status_detail: string | null;
  status_at: string;
  event_count: number;
  company_count: number;
  /** Snapshot taken at send time: survives purged events. */
  events: {
    event_id: number | null;
    siren: string;
    company: string | null;
    kind: string;
    title: string;
    severity: Severity;
  }[];
}

export interface AlertLogPage {
  items: AlertDelivery[];
  total: number;
  page: number;
  page_size: number;
}

export interface EventKindInfo {
  kind: string;
  label: string;
  description: string;
  severity: Severity;
}

export interface WatchCreated {
  id: number | null;
  siren: string;
  name: string | null;
  status: "pending_confirmation" | "pending_approval" | "active";
}

export interface UnsubscribeInfo {
  siren: string;
  name: string | null;
  active: boolean;
}

export type ImportRowStatus = "new" | "update" | "duplicate" | "invalid";

export interface ImportRow {
  line: number;
  raw: string;
  siren: string | null;
  label: string | null;
  categories: string[];
  status: ImportRowStatus;
  message: string | null;
}

export interface ImportPreview {
  filename: string;
  siren_column: string;
  label_column: string | null;
  category_column: string | null;
  rows: ImportRow[];
}

export type ImportResultStatus = "created" | "updated" | "not_found" | "error";

export interface ImportJob {
  id: number;
  filename: string;
  status: "pending" | "running" | "done";
  total: number;
  processed: number;
  created_at: string;
  finished_at: string | null;
  results: {
    line: number | null;
    siren: string;
    name: string | null;
    status: ImportResultStatus;
    message: string | null;
  }[];
}

export interface ExportJob {
  id: number;
  filename: string;
  status: "pending" | "running" | "done" | "failed";
  total: number;
  processed: number;
  /** PDFs in the zip, and PDFs INSEE or INPI did not deliver (detailed in the spreadsheet). */
  documents: number;
  missing: number;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  expires_at: string | null;
  /** Null until the zip is ready, and again once it has expired. */
  download_url: string | null;
}

export interface BulkChange {
  watch_ids: number[];
  add_category_ids?: number[];
  remove_category_ids?: number[];
  stop?: boolean;
}

export interface RegistrationInfo {
  open: boolean;
  approval_required: boolean;
}

// --- Admin -----------------------------------------------------------------------

export interface RegistrationSettings {
  registrations_open: boolean;
  approval_required: boolean;
}

export type UserStatus = "all" | "pending" | "unconfirmed" | "active" | "admin";

export interface AdminUser {
  id: number;
  email: string;
  created_at: string;
  confirmed_at: string | null;
  approved: boolean;
  is_admin: boolean;
  brand: string | null;
  watch_count: number;
}

export interface AdminUserPage {
  items: AdminUser[];
  total: number;
  pending_approval: number;
}

export interface DayCount {
  day: string;
  count: number;
}

export interface AdminStats {
  users: Record<"total" | "confirmed" | "unconfirmed" | "pending_approval" | "admins" | "new_7d" | "new_30d", number>;
  watches: Record<"active" | "stopped" | "companies_watched" | "companies" | "categories", number>;
  events: Record<"total" | "history" | "last_30d", number>;
  notifications: Record<"sent_30d" | "skipped_30d", number>;
  imports: Record<"running" | "total", number>;
  /** One entry per day over 30 days, oldest first. */
  signups: DayCount[];
  alerts: DayCount[];
  events_by_kind: { kind: string; label: string; severity: Severity; count: number }[];
  sources: { source: string; companies: number; failing: number; last_success_at: string | null }[];
  registration: RegistrationSettings;
}

export type ColumnKind = "integer" | "number" | "boolean" | "string" | "text" | "datetime" | "date" | "json";

export interface AdminColumn {
  name: string;
  kind: ColumnKind;
  nullable: boolean;
  primary_key: boolean;
  /** Filled by the application or database when left out on creation. */
  has_default: boolean;
  max_length: number | null;
  /** "table.column" */
  foreign_key: string | null;
}

export interface AdminTable {
  name: string;
  columns: AdminColumn[];
  primary_key: string[];
  row_count: number;
}

export type RowKey = (string | number | boolean | null)[];

export interface AdminRow {
  key: RowKey;
  /** In lists, JSON and long text are shortened previews. */
  values: Record<string, unknown>;
}

export interface RowQuery {
  q?: string;
  where?: Record<string, unknown>;
  sort?: string | null;
  desc?: boolean;
  page?: number;
}

interface Message {
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const FALLBACK: Record<number, string> = {
  401: "Votre session a expiré. Reconnectez-vous.",
  403: "Accès refusé.",
  404: "Élément introuvable.",
  429: "Trop de demandes. Réessayez dans quelques minutes.",
  503: "Un service public de données ne répond pas. Réessayez dans un instant.",
};

function messageFrom(status: number, body: unknown): string {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const fields = detail.flatMap((d) => (d as { loc?: unknown[] }).loc ?? []);
    if (fields.includes("email")) return "Cette adresse e-mail n'est pas valide.";
    return "Vérifiez les informations saisies.";
  }
  return FALLBACK[status] ?? "Le service est momentanément indisponible. Réessayez plus tard.";
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  const isForm = body instanceof FormData;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: "same-origin",
      // FormData sets its own multipart boundary header.
      headers: body === undefined || isForm ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "Connexion impossible. Vérifiez votre accès à Internet.");
  }
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(response.status, messageFrom(response.status, data));
  return data as T;
}

/** Download a file from our API, surfacing the server's error message on failure. */
export async function downloadFile(url: string, fallbackName: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url, { credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "Connexion impossible. Vérifiez votre accès à Internet.");
  }
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, messageFrom(response.status, data));
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const href = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = href;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

const enc = encodeURIComponent;

export const api = {
  search: (q: string) => request<CompanySummary[]>("GET", `/search?q=${enc(q)}`),
  summary: (siren: string) => request<CompanySummary>("GET", `/companies/${enc(siren)}/summary`),
  createWatch: (body: { siren: string; email?: string; label?: string; category_ids?: number[] }) =>
    request<WatchCreated>("POST", "/watches", body),
  confirm: (token: string) => request<Message>("POST", `/confirm/${enc(token)}`),
  unsubscribeInfo: (token: string) => request<UnsubscribeInfo>("GET", `/unsubscribe/${enc(token)}`),
  unsubscribe: (token: string) => request<UnsubscribeInfo>("POST", `/unsubscribe/${enc(token)}`),

  login: (email: string) => request<Message>("POST", "/auth/login", { email }),
  verify: (token: string) => request<Me>("POST", "/auth/verify", { token }),
  logout: () => request<Message>("POST", "/auth/logout"),
  me: () => request<Me | null>("GET", "/me"),
  registration: () => request<RegistrationInfo>("GET", "/registration"),
  setAlertKinds: (alert_kinds: string[]) => request<Me>("PUT", "/me/alerts", { alert_kinds }),
  eventKinds: () => request<EventKindInfo[]>("GET", "/event-kinds"),
  deleteAccount: () => request<Message>("DELETE", "/me"),
  recipients: () => request<Recipient[]>("GET", "/me/recipients"),
  addRecipient: (email: string) => request<Recipient>("POST", "/me/recipients", { email }),
  removeRecipient: (id: number) => request<Message>("DELETE", `/me/recipients/${id}`),
  resendRecipient: (id: number) => request<Message>("POST", `/me/recipients/${id}/resend`),
  reactivateRecipient: (email: string) => request<Recipient[]>("POST", "/me/recipients/reactivate", { email }),
  confirmRecipient: (token: string) => request<Message & { email: string }>("POST", "/recipients/confirm", { token }),
  leaveRecipients: (token: string) => request<Message>("POST", `/recipients/unsubscribe/${enc(token)}`),
  alertLog: (page: number, pageSize: number) =>
    request<AlertLogPage>("GET", `/me/alert-log?${new URLSearchParams({ page: String(page), page_size: String(pageSize) })}`),

  watches: () => request<WatchItem[]>("GET", "/watches"),
  updateWatch: (id: number, change: { label?: string; category_ids?: number[] }) =>
    request<WatchItem>("PATCH", `/watches/${id}`, change),
  bulkWatches: (change: BulkChange) => request<{ updated: number }>("POST", "/watches/bulk", change),
  removeWatch: (id: number) => request<Message>("DELETE", `/watches/${id}`),
  company: (siren: string) => request<CompanyDetail>("GET", `/companies/${enc(siren)}`),

  categories: () => request<Category[]>("GET", "/categories"),
  createCategory: (name: string) => request<Category>("POST", "/categories", { name }),
  renameCategory: (id: number, name: string) => request<Category>("PATCH", `/categories/${id}`, { name }),
  deleteCategory: (id: number) => request<Message>("DELETE", `/categories/${id}`),

  previewImport: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<ImportPreview>("POST", "/imports/preview", form);
  },
  startImport: (body: {
    filename: string;
    rows: { line: number; siren: string; label: string | null; categories: string[] }[];
    category_ids: number[];
  }) => request<ImportJob>("POST", "/imports", body),
  imports: () => request<ImportJob[]>("GET", "/imports"),
  importJob: (id: number) => request<ImportJob>("GET", `/imports/${id}`),

  startExport: (watchIds: number[]) => request<ExportJob>("POST", "/exports", { watch_ids: watchIds }),
  exports: () => request<ExportJob[]>("GET", "/exports"),

  admin: {
    stats: () => request<AdminStats>("GET", "/admin/stats"),
    settings: () => request<RegistrationSettings>("GET", "/admin/settings"),
    saveSettings: (value: RegistrationSettings) => request<RegistrationSettings>("PUT", "/admin/settings", value),
    users: (params: { q: string; status: UserStatus; page: number }) =>
      request<AdminUserPage>(
        "GET",
        `/admin/users?${new URLSearchParams({ q: params.q, status: params.status, page: String(params.page) })}`,
      ),
    updateUser: (id: number, change: { approved?: boolean; is_admin?: boolean }) =>
      request<AdminUser>("PATCH", `/admin/users/${id}`, change),
    approve: (body: { user_ids?: number[]; all_pending?: boolean }) =>
      request<Message>("POST", "/admin/users/approve", body),
    deleteUser: (id: number) => request<Message>("DELETE", `/admin/users/${id}`),

    tables: () => request<AdminTable[]>("GET", "/admin/tables"),
    rows: (table: string, query: RowQuery) => {
      const params = new URLSearchParams({
        q: query.q ?? "",
        where: JSON.stringify(query.where ?? {}),
        desc: String(query.desc ?? true),
        page: String(query.page ?? 1),
      });
      if (query.sort) params.set("sort", query.sort);
      return request<{ rows: AdminRow[]; total: number }>("GET", `/admin/tables/${enc(table)}/rows?${params}`);
    },
    row: (table: string, key: RowKey) =>
      request<AdminRow>("GET", `/admin/tables/${enc(table)}/row?key=${enc(JSON.stringify(key))}`),
    createRow: (table: string, values: Record<string, unknown>) =>
      request<AdminRow>("POST", `/admin/tables/${enc(table)}/rows`, { values }),
    updateRow: (table: string, key: RowKey, values: Record<string, unknown>) =>
      request<AdminRow>("PUT", `/admin/tables/${enc(table)}/row?key=${enc(JSON.stringify(key))}`, { values }),
    deleteRow: (table: string, key: RowKey) =>
      request<Message>("DELETE", `/admin/tables/${enc(table)}/row?key=${enc(JSON.stringify(key))}`),
  },
};
